import {Component, computed, DestroyRef, effect, inject, input, output, signal, viewChild} from '@angular/core';
import {FullTaDetails} from '../../../api';
import {Card} from 'primeng/card';
import {TaCostBubbleComponent} from '../ta-cost-bubble/ta-cost-bubble.component';
import {Button} from 'primeng/button';
import {DatePipe, NgClass, NgTemplateOutlet} from '@angular/common';
import {Router} from '@angular/router';
import {TaAppStore} from '../../../state/clcta-store';
import {TaSubmissionDatasource} from '../../../datasources/ta-submission.datasource';
import {
  canCreateTemplateFrom,
  isFlightOnlyTa,
  TaCardAction,
  TaCommentCapabilities,
  taCommentCapabilitiesFor,
  toTAStatusText,
} from '../../../util/app.types';
import {PermissionService} from '../../../services/permission.service';
import {TaNavigationService} from '../../../services/ta-navigation.service';
import {Tooltip} from 'primeng/tooltip';
import {Tag} from 'primeng/tag';
import {TaCommentsComponent} from '../ta-comments/ta-comments';
import {ScrollHeightDirective} from '../../directives/scroll-height.directive';
import {taOverpaidAmount, taPaymentComponents} from '../../../util/general.utils';

/**
 * Below this the summary column is too narrow for the label/value grid, so the card defaults to
 * button-only and the user opts back into the details.
 */
export const FULL_DETAILS_MIN_WIDTH = '(min-width: 1300px)';

@Component({
  selector: 'ta-info-card',
  imports: [
    Card,
    TaCostBubbleComponent,
    Button,
    DatePipe,
    NgClass,
    NgTemplateOutlet,
    Tooltip,
    Tag,
    TaCommentsComponent,
    ScrollHeightDirective,
  ],
  templateUrl: './ta-info-card.component.html',
  styleUrl: './ta-info-card.component.scss'
})
export class TaInfoCardComponent {
  router = inject(Router);
  store = inject(TaAppStore);
  permissionService = inject(PermissionService);
  taSubmissionDatasource = inject(TaSubmissionDatasource);
  private taNavigation = inject(TaNavigationService);
  selectedTa = input<FullTaDetails | undefined>(undefined);
  viewOnly = input<boolean>(false);
  infoContext = input<undefined | 'manager' | 'finance' | 'compliance' | 'home'>(undefined);
  // Mirrors the value columns of the dashboard table the TA was picked from; unset = default stack.
  costView = input<'finalisation' | 'prepayment' | 'overpaid' | 'dayTrip' | undefined>(undefined);
  // Home only: offers saving the TA as a template.
  showCreateTemplate = input<boolean>(false);
  // Finance payment day for the prepayment (CLC-153); shown on the prepayment and overpaid views.
  scheduledPaymentDate = input<string | undefined>(undefined);
  actionTa = output<TaCardAction>();
  createTemplate = output<void>();

  private viewportFitsDetails = signal<boolean>(window.matchMedia(FULL_DETAILS_MIN_WIDTH).matches);
  // Set by the toggle, cleared whenever the viewport crosses the breakpoint so the card goes back
  // to following the screen width rather than a choice made at some other size.
  private detailsOverride = signal<boolean | null>(null);

  // Which of the card's two modes is showing: the full details, or the toggle button on its own.
  showDetails = computed<boolean>(() => this.detailsOverride() ?? this.viewportFitsDetails());
  toggleLabel = computed<string>(() => this.showDetails() ? 'Hide TA summary' : 'Show TA summary');

  /**
   * Whether the summary section is unfolded. The card's two sections fold independently: the
   * figures and the conversation are read at different times, and either can have the whole card.
   */
  summaryOpen = signal<boolean>(true);

  // Today is past the scheduled payment day.
  prePaymentOverdue = computed<boolean>(() => {
    const scheduled = this.scheduledPaymentDate();
    if (!scheduled) {
      return false;
    }
    const due = new Date(scheduled);
    due.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today > due;
  });
  summaryToggleLabel = computed<string>(
    () => this.summaryOpen() ? 'Hide summary details' : 'Show summary details');

  /** The chain, so its height can be re-measured once a fold above it has moved it. */
  private taComments = viewChild(TaCommentsComponent);

  taCommentsId = computed<string | undefined>(() => this.selectedTa()?.id ?? undefined);
  // What the chain lets the user do, worked out here rather than in the component - see
  // taCommentCapabilitiesFor.
  taCommentCapabilities = computed<TaCommentCapabilities>(
    () => taCommentCapabilitiesFor(this.selectedTa()?.status));
  canSaveAsTemplate = computed<boolean>(
    () => this.showCreateTemplate() && canCreateTemplateFrom(this.selectedTa()));

  originalTaValue = computed<number>(() => (
    this.selectedTa()?.originalValue ?? 0
  ));
  currentTaValue = computed<number>(() => (
    this.selectedTa()?.currentValue ?? 0
  ));
  // Same fallback as the table cells.
  totalTaValue = computed<number>(() => this.selectedTa()?.currentValue ?? this.selectedTa()?.originalValue ?? 0);
  isPreApproval = computed<boolean>(() => this.selectedTa()?.status === 'InProgress' || this.selectedTa()?.status === 'PendingApproval');
  // Approved but not yet in acquittal: one value, as on the manage pages.
  isApprovedPreAcquittal = computed<boolean>(() => this.selectedTa()?.status === 'TravelApproved' || this.selectedTa()?.status === 'PendingCancelApproval');
  acquittedValueChange = computed<number>(() => this.currentTaValue() - this.originalTaValue());
  // Not "Acquitted" until the acquittal has been submitted.
  currentValueTitle = computed<string>(() => this.selectedTa()?.status === 'PendingAcquittal' ? 'Pending Acquittal TA Value' : 'Acquitted TA Value');
  isPrePaid = computed<boolean>(() => !!this.selectedTa()?.isPrePaid);
  isFlightOnly = computed<boolean>(() => isFlightOnlyTa(this.selectedTa()));
  // Same figures the manage pages show, from the selected row's values.
  paymentComponents = computed(() => taPaymentComponents(this.selectedTa() ?? {}));
  overpaymentMade = computed<number>(() => taOverpaidAmount(this.selectedTa() ?? {}));

  constructor() {
    const viewport = window.matchMedia(FULL_DETAILS_MIN_WIDTH);
    const onViewportChange = (event: MediaQueryListEvent) => {
      this.viewportFitsDetails.set(event.matches);
      this.detailsOverride.set(null);
    };
    viewport.addEventListener('change', onViewportChange);
    inject(DestroyRef).onDestroy(() => viewport.removeEventListener('change', onViewportChange));

    // Every selection starts with the summary open: the figures are what the user clicked the row
    // for, and a fold left over from the last TA would hide them. The chain does the same for
    // itself, off its own taId.
    effect(() => {
      this.selectedTa();
      this.summaryOpen.set(true);
    });
  }

  toggleSummary(): void {
    this.summaryOpen.update(open => !open);
  }

  /**
   * The chain is capped against the bottom of the window, so folding the summary changes how much
   * room it has. Waits for the fold to finish rather than measuring mid-transition, and only for
   * the fold's own property - transitionend bubbles, and the buttons inside have their own.
   */
  onSummaryFolded(event: TransitionEvent): void {
    if (event.propertyName !== 'grid-template-rows') {
      return;
    }
    this.taComments()?.measure();
  }

  toggleDetails(): void {
    this.detailsOverride.set(!this.showDetails());
  }

  viewTa(): void {
    // Home opens the TA the way the list's Open button does.
    const ta = this.selectedTa();
    if (this.infoContext() === 'home' && ta) {
      this.taNavigation.open(ta);
      return;
    }
    this.store.updatePreviousPageRoute(this.router.url);
    this.router.navigate([`/ta/${this.selectedTa()?.id}/manage`]);
  }

  getDuration(): string {
    if (!this.selectedTa()?.startDate || !this.selectedTa()?.endDate) {
      return '-';
    }
    let start = new Date(this.selectedTa()?.startDate ?? '');
    let end = new Date(this.selectedTa()?.endDate ?? '');

    let numOfDays = Math.round((end.getTime() - start.getTime()) / 1000 / 3600 / 24) + 1;
    if (numOfDays === 1) {
      return '1 day (0 nights)';
    }
    if (numOfDays <= 0) {
      return '0 days (0 nights)';
    }
    return (numOfDays) + ' days (' + (numOfDays - 1) + ' nights)';
  }

  protected readonly toTAStatusText = toTAStatusText;
}
